import Box from '@mui/material/Box';
import React, { memo, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import facebookImage from '../../assets/VS_Facebook.jpg';
import twitterImage from '../../assets/VS_Twitter.jpg';
import config from '../../config';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import routeConfiguration from '../../routeConfiguration';
import useAppSelector from '../../util/hooks/useAppSelector';
import useScreenSize from '../../util/hooks/useScreenSize';
import { isCordova } from '../../util/mobile/deviceDetection';
import { canonicalRoutePath, changeLocale, pathByRouteName } from '../../util/routes';
import { generatePageSchema, metaTagProps } from '../../util/seo';
import CookieConsent from '../CookieConsent/CookieConsent';
import PortalModalContainer from '../PortalModalContainer/PortalModalContainer';

const preventDefault = (e) => {
  e.preventDefault();
};

/**
 * Page component
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - The child elements to render
 * @param {string | null} props.author - The author of the page
 * @param {string | null} props.contentType - The content type of the page
 * @param {string | null} props.description - The description of the page
 * @param {Array<object> | null} props.facebookImages - The facebook images of the page
 * @param {Array<object> | null} props.twitterImages - The twitter images of the page
 * @param {string | null} props.published - The published date of the page
 * @param {string | null} props.referrer - The referrer of the page
 * @param {object | null} props.schema - The schema of the page
 * @param {string | null} props.tags - The tags of the page
 * @param {string | null} props.twitterHandle - The twitter handle of the page
 * @param {string | null} props.updated - The updated date of the page
 * @param {Array<string> | null} props.specificLocals - The specific locals of the page
 * @param {Array<{hrefLang: string, href: string}> | null} props.alternateLinks - Explicit hreflang alternates (overrides specificLocals)
 * @param {string | null} props.title - The title of the page
 * @param {Array<string> | null} props.keywords - The keywords of the page
 * @param {Array<React.ReactNode> | null} props.customScripts - The custom scripts of the page
 */
const PageComponent = ({
  children,
  author = null,
  contentType = 'website',
  description = '',
  facebookImages = null,
  twitterImages = null,
  published = null,
  referrer = null,
  schema = {},
  tags = null,
  twitterHandle = null,
  updated = null,
  specificLocals = null,
  alternateLinks = null,
  title,
  keywords = [],
  customScripts = [],
}) => {
  const intl = useIntl();
  const location = useLocation();

  const [scrollPosition, setScrollPosition] = useState(0);

  const scrollingDisabled = useAppSelector((state) =>
    isScrollingDisabled(state.ui.disableScrollRequests),
  );
  const [scrollingDisabledState, setScrollingDisabledState] = useState(false);

  const contentDivRef = useRef(null);

  useEffect(() => {
    // By default a dropped file is loaded in the browser window as a
    // file URL. We want to prevent this since it might loose a lot of
    // data the user has typed but not yet saved. Preventing requires
    // handling both dragover and drop events.
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    // Remove duplicated server-side rendered page schema.
    // It's in <body> to improve initial rendering performance,
    // but after web app is initialized, react-helmet-async operates with <head>
    const pageSchema = document.getElementById('page-schema');
    if (pageSchema) {
      pageSchema.remove();
    }
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  const scrollingDisabledChanged = (currentScrollingDisabled) => {
    if (currentScrollingDisabled && currentScrollingDisabled !== scrollingDisabledState) {
      // Update current scroll position, if scrolling is disabled (e.g. modal is open)
      setScrollPosition(window.pageYOffset || document.documentElement.scrollTop);
      setScrollingDisabledState(currentScrollingDisabled);
    } else if (currentScrollingDisabled !== scrollingDisabledState) {
      setScrollingDisabledState(currentScrollingDisabled);
    }
  };

  scrollingDisabledChanged(scrollingDisabled);
  const referrerMeta = referrer ? <meta content={referrer} name="referrer" /> : null;

  const canonicalRootURL = config.canonicalRootURL;
  const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
  const canonicalPath = canonicalRoutePath(
    routeConfiguration(),
    location,
    shouldReturnPathOnly,
    intl.locale,
  );
  const canonicalUrl = `${canonicalRootURL}${canonicalPath}`;

  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'Page.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'Page.schemaDescription' });
  const metaTitle = title || schemaTitle;
  const metaDescription = description || schemaDescription;
  const facebookImgs = facebookImages || [
    {
      name: 'facebook',
      url: `${canonicalRootURL}${facebookImage}`,
      width: 1200,
      height: 630,
    },
  ];
  const twitterImgs = twitterImages || [
    {
      name: 'twitter',
      url: `${canonicalRootURL}${twitterImage}`,
      width: 600,
      height: 314,
    },
  ];

  const metaToHead = metaTagProps({
    author,
    contentType,
    description: metaDescription,
    facebookImages: facebookImgs,
    twitterImages: twitterImgs,
    published,
    tags,
    title: metaTitle,
    twitterHandle,
    updated,
    url: canonicalUrl,
    locale: intl.locale,
  });

  const metaTags = metaToHead.map((metaProps, i) => <meta key={i} {...metaProps} />);

  const keywordsMetaTag =
    keywords && keywords.length ? <meta content={keywords.join(', ')} name="keywords" /> : null;

  // Storyblok pages pass explicit hreflang alternates with the correct per-language
  // slug for each published translation. Other pages derive alternates by swapping the
  // locale segment of the (now locale-complete) canonical URL.
  const localTags =
    alternateLinks && alternateLinks.length
      ? alternateLinks.map(({ hrefLang, href }, i) => (
        <link href={href} hrefLang={hrefLang} key={i} rel="alternate" />
      ))
      : (specificLocals || Object.keys(config.languages)).map((locale, i) => (
        <link
          href={`${changeLocale(canonicalUrl, locale)}${location.search}`}
          hrefLang={locale}
          key={i}
          rel="alternate"
        />
      ));

  const xDefaultHref =
    alternateLinks && alternateLinks.length
      ? (alternateLinks.find((alt) => alt.hrefLang === config.locale) || {}).href ||
        changeLocale(canonicalUrl, config.locale)
      : `${changeLocale(canonicalUrl, config.locale)}${location.search}`;

  localTags.push(
    <link href={xDefaultHref} hrefLang="x-default" key="x-default" rel="alternate" />,
  );
  
  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format

  // Schema attribute can be either single schema object or an array of objects
  // This makes it possible to include several different items from the same page.
  // E.g. Product, Place, Video


  // If scrolling is not disabled, but content element has still scrollPosition set
  // in style attribute, we scrollTo scrollPosition.
  const hasMarginTopStyle = contentDivRef?.current && contentDivRef.current.style.marginTop;
  if (!scrollingDisabled && hasMarginTopStyle) {
    window.requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  }

  const scrollPositionStyles = scrollingDisabled ? { marginTop: `${-1 * scrollPosition}px` } : {};

  const { isMobile, isTablet } = useScreenSize();
  const mobileStyles =
    isCordova() || isTablet || isMobile ? { minHeight: '100vh', justifyContent: 'center' } : {};

  const scrollingDisabledStyles = scrollingDisabledState
    ? {
      position: 'fixed',
      width: '100vw',
      overflow: 'hidden',
    }
    : {};

  const pageSchemaArray = !isCordova() ? (
    <script id="page-schema" type="application/ld+json">
      {generatePageSchema(schemaTitle, schemaDescription, schema)}
    </script>
  ) : null;

  const isSearchPage =
    location.pathname === pathByRouteName('SearchPage', routeConfiguration(), {}, intl.locale);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        ...scrollingDisabledStyles,
      }}
    >
      <Helmet htmlAttributes={{ lang: intl.locale }}>
        <title>{title}</title>
        {referrerMeta}
        <link href={canonicalUrl} rel="canonical" />
        <meta content="text/html; charset=UTF-8" httpEquiv="Content-Type" />
        <meta content={intl.locale} httpEquiv="Content-Language" />
        {isSearchPage ? <meta content="noindex, nofollow" name="robots" /> : null}
        {metaTags}
        {keywordsMetaTag}
        {localTags}
        {pageSchemaArray}
        {customScripts.map((script, i) => (
          <script defer key={i} src={script} />
        ))}
      </Helmet>
      <CookieConsent />
      <PortalModalContainer />
      <Box
        ref={contentDivRef}
        style={{
          ...scrollPositionStyles,
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          ...mobileStyles,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

PageComponent.displayName = 'Page';

const Page = memo(PageComponent);

export default Page;